import { useEffect, useMemo, useState } from 'react';

import { useGetAllVacancyCandidates } from '@entities/vacancy';

import { ALL_PENDING_CANDIDATES_PARAMS } from '../constants';
import type { CandidatePickVacancyMeta } from '../types';
import { mapCandidateToPickCard } from '../utils/mapCandidateToPickCard';

type UseCandidatePickDrawerArgs = CandidatePickVacancyMeta & {
  vacancyId: number;
  currency: string;
  paymentPeriod: string;
  opened: boolean;
};

export const useCandidatePickDrawer = ({
  vacancyId,
  currency,
  paymentPeriod,
  opened,
}: UseCandidatePickDrawerArgs) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [listViewOpen, setListViewOpen] = useState(false);
  const candidatesQuery = useGetAllVacancyCandidates(vacancyId, {
    ...ALL_PENDING_CANDIDATES_PARAMS,
    enabled: opened,
  });
  const candidates = useMemo(
    () =>
      (candidatesQuery.data ?? []).map(candidate =>
        mapCandidateToPickCard(candidate, { currency, paymentPeriod })
      ),
    [candidatesQuery.data, currency, paymentPeriod]
  );

  useEffect(() => {
    if (opened) {
      setCurrentIndex(0);
      setListViewOpen(false);
    }
  }, [opened]);

  const handleIndexChange = (index: number) => {
    if (index >= 0 && index < candidates.length) {
      setCurrentIndex(index);
    }
  };

  const handleListCandidateSelect = (index: number) => {
    handleIndexChange(index);
    setListViewOpen(false);
  };

  return {
    candidates,
    currentIndex,
    listViewOpen,
    onIndexChange: handleIndexChange,
    onListViewOpen: () => setListViewOpen(true),
    onListViewClose: () => setListViewOpen(false),
    onListCandidateSelect: handleListCandidateSelect,
    isLoading: candidatesQuery.isLoading,
    isError: candidatesQuery.isError,
  };
};
